import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ThreeEvent, useThree } from "@react-three/fiber";
import { observer } from "mobx-react-lite";
import * as THREE from "three";
import { FloorplannerStoreContext } from "../../../../../store/floorplannerStore";
import { SymbolType, WindowType } from "../../../../../types/floorplannerTypes";
import { resolveMaterial } from "../../../materials/materialResolver";
import { shouldUseSymbolFillColorIn3D } from "../../../materials/primitiveSymbolFill";
import SelectedMeshOutline from "../../../scene/SelectedMeshOutline";
import { usePointerUpSelection } from "../../../scene/usePointerUpSelection";
import {
  Window3DAssemblyGeometry,
  Window3DBoxGeometry,
  Window3DLeafGeometry,
  clampWindowOpenAngle,
  getSymbolColor,
} from "../openingGeometry";
import { CANVAS_ACCENT_COLOR_HEX } from "../../../../../constants/brandColors";
import { resolveFillOpacity } from "../../../../../utils/fillOpacity";

const Window3DAssemblyMesh: React.FC<{
  symbol: WindowType;
  assembly: Window3DAssemblyGeometry;
  selected: boolean;
  claimSelectionEvents?: boolean;
  onSelect: (symbol: SymbolType, event: ThreeEvent<PointerEvent>) => void;
  onAngleDragActiveChange: (active: boolean) => void;
}> = observer(
  ({
    symbol,
    assembly,
    selected,
    claimSelectionEvents = false,
    onSelect,
    onAngleDragActiveChange,
  }) => {
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const { gl, invalidate } = useThree();
    const [dragging, setDragging] = useState(false);
    const draggingRef = useRef(false);
    const startAngleRef = useRef(symbol.openAngle ?? 0);
    const latestAngleRef = useRef(symbol.openAngle ?? 0);
    const changedAngleRef = useRef(false);
    const leafGroupRef = useRef<THREE.Group | null>(null);
    const selectionHandlers = usePointerUpSelection(symbol, onSelect, {
      claimPointerEvents: claimSelectionEvents,
    });

    // Independent horizontal and vertical spröjs; both default to 0 (no bars)
    const horizontalMuntinCount = resolveHorizontalMuntinCount(symbol);
    const verticalMuntinCount = resolveVerticalMuntinCount(symbol);

    // Dynamically calculate spröjs (muntin) bar geometries across the glass pane
    const muntins = useMemo<Window3DBoxGeometry[]>(() => {
      const glass = assembly.leaf.glassPane;
      const barThickness = 0.022;
      const barDepth = Math.max(glass.size.y + 0.008, 0.02);
      const bars: Window3DBoxGeometry[] = [];

      if (horizontalMuntinCount > 0) {
        const startZ = glass.center.z - glass.size.z / 2;
        const stepZ = glass.size.z / (horizontalMuntinCount + 1);
        for (let index = 0; index < horizontalMuntinCount; index += 1) {
          bars.push({
            id: `${assembly.leaf.id}-muntin-h-${index}`,
            center: new THREE.Vector3(
              glass.center.x,
              glass.center.y,
              startZ + stepZ * (index + 1),
            ),
            size: new THREE.Vector3(glass.size.x, barDepth, barThickness),
          });
        }
      }

      if (verticalMuntinCount > 0) {
        const startX = glass.center.x - glass.size.x / 2;
        const stepX = glass.size.x / (verticalMuntinCount + 1);
        for (let index = 0; index < verticalMuntinCount; index += 1) {
          bars.push({
            id: `${assembly.leaf.id}-muntin-v-${index}`,
            center: new THREE.Vector3(
              startX + stepX * (index + 1),
              glass.center.y,
              glass.center.z,
            ),
            size: new THREE.Vector3(barThickness, barDepth, glass.size.z),
          });
        }
      }

      return bars;
    }, [
      assembly.leaf.glassPane,
      assembly.leaf.id,
      horizontalMuntinCount,
      verticalMuntinCount,
    ]);

    useEffect(() => {
      draggingRef.current = dragging;
    }, [dragging]);

    useEffect(() => {
      latestAngleRef.current = symbol.openAngle ?? 0;
      applyOpenAngleToLeafGroup(
        assembly.leaf,
        leafGroupRef.current,
        symbol.openAngle ?? 0,
      );
    }, [assembly.leaf, symbol.openAngle]);

    const completeAngleDrag = useCallback(
      (updateState = true) => {
        if (!draggingRef.current) return;

        draggingRef.current = false;
        if (updateState) {
          setDragging(false);
        }
        onAngleDragActiveChange(false);
        gl.domElement.style.cursor = "default";

        if (
          changedAngleRef.current &&
          Math.abs(latestAngleRef.current - startAngleRef.current) > 1e-5
        ) {
          floorplannerStore.updateSymbolProperty(
            symbol.id,
            "openAngle",
            latestAngleRef.current,
          );
          floorplannerStore.recordSymbolChange(symbol.id, "modify");
          floorplannerStore.pushToUndoStack();
          floorplannerStore.setBlockDirty(false);
          floorplannerStore.setDirty();
        } else {
          floorplannerStore.cancelUndoTransaction();
          floorplannerStore.setBlockDirty(false);
        }
        invalidate();
      },
      [
        floorplannerStore,
        gl.domElement.style,
        invalidate,
        onAngleDragActiveChange,
        symbol.id,
      ],
    );

    const completeAngleDragRef = useRef(completeAngleDrag);
    useEffect(() => {
      completeAngleDragRef.current = completeAngleDrag;
    }, [completeAngleDrag]);

    useEffect(() => {
      if (!selected) completeAngleDrag();
    }, [completeAngleDrag, selected]);

    useEffect(
      () => () => {
        completeAngleDragRef.current(false);
      },
      [],
    );

    const finishAngleDrag = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        if (!draggingRef.current) return;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          releasePointerCapture?: (pointerId: number) => void;
        };
        try {
          pointerTarget.releasePointerCapture?.(event.pointerId);
        } catch {
          // Pointer capture release safety catch
        }
        completeAngleDrag();
      },
      [completeAngleDrag],
    );

    const updateOpenAngleFromPointer = useCallback(
      (leaf: Window3DLeafGeometry, event: ThreeEvent<PointerEvent>) => {
        if (!dragging) return;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const nextPoint = getPointerPointOnZPlane(
          event,
          leaf.angleHandleCenter.z,
        );
        if (!nextPoint) return;

        const nextAngle = getWindowOpenAngleFromPointerPoint(
          assembly,
          leaf,
          nextPoint,
        );
        if (Math.abs(nextAngle - latestAngleRef.current) <= 1e-4) return;

        changedAngleRef.current = true;
        latestAngleRef.current = nextAngle;
        applyOpenAngleToLeafGroup(
          assembly.leaf,
          leafGroupRef.current,
          nextAngle,
        );
        invalidate();
      },
      [assembly, dragging, invalidate],
    );

    const startAngleDrag = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopImmediatePropagation?.();
        const pointerTarget = event.target as unknown as {
          setPointerCapture?: (pointerId: number) => void;
        };
        pointerTarget.setPointerCapture?.(event.pointerId);
        startAngleRef.current = symbol.openAngle ?? 0;
        latestAngleRef.current = symbol.openAngle ?? 0;
        changedAngleRef.current = false;
        floorplannerStore.beginUndoTransaction("updateWindowOpenAngle3D");
        floorplannerStore.recordSymbolForUndo(symbol.id);
        floorplannerStore.setBlockDirty(true);
        draggingRef.current = true;
        setDragging(true);
        onAngleDragActiveChange(true);
        gl.domElement.style.cursor = "grabbing";
        invalidate();
      },
      [
        floorplannerStore,
        gl.domElement.style,
        invalidate,
        onAngleDragActiveChange,
        symbol.id,
        symbol.openAngle,
      ],
    );

    const symbol3DOnly = { threeD: symbol.threeD };
    const symbolPartOwner = shouldUseSymbolFillColorIn3D(symbol)
      ? symbol
      : symbol3DOnly;
    const frameColor = resolveMaterial("doorFrame", symbolPartOwner, {
      defaultColor: getSymbolColor(symbol).lerp(
        new THREE.Color(0xffffff),
        0.64,
      ),
    }).color;
    const sashColor = frameColor.clone().lerp(new THREE.Color(0xffffff), 0.18);
    const grooveColor = frameColor.clone().multiplyScalar(0.72);
    /**
     * Keep the pane as default glass — do not apply symbol fillColor, or the
     * glass tints with the frame. Fill only drives karm (casings/frames) and
     * spröjs (muntins) via frameColor / sashColor.
     */
    const glassColor = resolveMaterial("windowGlass", symbol3DOnly, {
      defaultColor: 0x8cc9e8,
    }).color;
    const hardwareColor = resolveMaterial("doorHardware", symbolPartOwner, {
      defaultColor: 0xc7cbd0,
    }).color;
    const fillOpacity = resolveFillOpacity(symbol);
    const opacityMaterialProps = {
      opacity: fillOpacity,
      transparent: fillOpacity < 1,
      depthWrite: fillOpacity >= 1,
    };
    const glassOpacity = 0.36 * fillOpacity;

    return (
      <group
        position={[assembly.origin.x, assembly.origin.y, 0]}
        rotation={[0, 0, assembly.rotation]}
        userData={{ floorplannerType: symbol.type, floorplannerId: symbol.id }}
        {...selectionHandlers}
        onPointerOver={() => {
          gl.domElement.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          if (!dragging) gl.domElement.style.cursor = "default";
        }}
      >
        {assembly.frames.map((frame) => (
          <BoxMesh
            key={frame.id}
            box={frame}
            color={sashColor}
            selected={selected}
            roughness={0.72}
            {...opacityMaterialProps}
          />
        ))}

        {assembly.casings.map((casing) => (
          <BoxMesh
            key={casing.id}
            box={casing}
            color={frameColor}
            selected={selected}
            roughness={0.7}
            {...opacityMaterialProps}
          />
        ))}

        {assembly.miterGrooves.map((groove) => (
          <BoxMesh
            key={groove.id}
            box={groove}
            color={grooveColor}
            roughness={0.78}
            {...opacityMaterialProps}
          />
        ))}

        <group
          ref={leafGroupRef}
          position={[assembly.leaf.hinge.x, assembly.leaf.hinge.y, 0]}
          rotation={[0, 0, assembly.leaf.rotation]}
        >
          <BoxMesh
            box={assembly.leaf.glassPane}
            color={glassColor}
            selected={selected}
            transparent
            opacity={glassOpacity}
            depthWrite={false}
            roughness={0.08}
          />

          {/* Render Spröjs (Muntins) */}
          {muntins.map((muntin) => (
            <BoxMesh
              key={muntin.id}
              box={muntin}
              color={sashColor}
              selected={selected}
              roughness={0.66}
              {...opacityMaterialProps}
            />
          ))}

          {assembly.leaf.sashFrames.map((frame) => (
            <BoxMesh
              key={frame.id}
              box={frame}
              color={sashColor}
              selected={selected}
              roughness={0.66}
              {...opacityMaterialProps}
            />
          ))}

          {assembly.leaf.hingeBarrels.map((barrel, index) => (
            <mesh
              key={`${assembly.leaf.id}-hinge-${index}`}
              position={[barrel.center.x, barrel.center.y, barrel.center.z]}
              rotation={[Math.PI / 2, 0, 0]}
              castShadow={fillOpacity >= 1}
              receiveShadow
            >
              <cylinderGeometry
                args={[barrel.radius, barrel.radius, barrel.height, 16]}
              />
              <meshStandardMaterial
                color={hardwareColor}
                roughness={0.24}
                metalness={0.66}
                {...opacityMaterialProps}
              />
              <SelectedMeshOutline visible={selected} />
            </mesh>
          ))}

          {assembly.leaf.handleCenters.map((handleCenter, index) => {
            const side = handleCenter.y >= 0 ? 1 : -1;
            const rosetteRadius = Math.max(assembly.leaf.depth * 0.28, 0.025);
            const rosetteDepth = Math.max(assembly.leaf.depth * 0.16, 0.01);
            const leverLength = Math.min(
              Math.max(assembly.leaf.width * 0.09, 0.08),
              0.13,
            );
            const leverThickness = Math.max(assembly.leaf.depth * 0.16, 0.012);
            const raisedY =
              handleCenter.y + side * (rosetteDepth / 2 + leverThickness / 2);

            return (
              <group key={`${assembly.leaf.id}-handle-${index}`}>
                <mesh
                  position={[handleCenter.x, handleCenter.y, handleCenter.z]}
                  castShadow={fillOpacity >= 1}
                  receiveShadow
                >
                  <cylinderGeometry
                    args={[rosetteRadius, rosetteRadius, rosetteDepth, 28]}
                  />
                  <meshStandardMaterial
                    color={hardwareColor}
                    roughness={0.22}
                    metalness={0.72}
                    {...opacityMaterialProps}
                  />
                  <SelectedMeshOutline visible={selected} />
                </mesh>
                <mesh
                  position={[
                    handleCenter.x,
                    raisedY,
                    handleCenter.z - rosetteRadius * 0.4 - leverLength / 2,
                  ]}
                  castShadow={fillOpacity >= 1}
                  receiveShadow
                >
                  <boxGeometry
                    args={[leverThickness * 1.45, leverThickness, leverLength]}
                  />
                  <meshStandardMaterial
                    color={hardwareColor}
                    roughness={0.22}
                    metalness={0.74}
                    {...opacityMaterialProps}
                  />
                  <SelectedMeshOutline visible={selected} />
                </mesh>
              </group>
            );
          })}

          {selected && (
            <group
              position={[
                assembly.leaf.angleHandleCenter.x,
                assembly.leaf.angleHandleCenter.y,
                assembly.leaf.angleHandleCenter.z,
              ]}
              onPointerDown={startAngleDrag}
              onPointerMove={(event) =>
                updateOpenAngleFromPointer(assembly.leaf, event)
              }
              onPointerUp={finishAngleDrag}
              onPointerCancel={finishAngleDrag}
              onLostPointerCapture={finishAngleDrag}
              onPointerOver={(event) => {
                event.stopPropagation();
                gl.domElement.style.cursor = dragging ? "grabbing" : "grab";
              }}
              onPointerOut={(event) => {
                event.stopPropagation();
                if (!dragging) gl.domElement.style.cursor = "pointer";
              }}
            >
              <mesh>
                <sphereGeometry args={[0.055, 20, 16]} />
                <meshBasicMaterial color={CANVAS_ACCENT_COLOR_HEX} />
              </mesh>
              <mesh>
                <sphereGeometry args={[0.11, 12, 10]} />
                <meshBasicMaterial transparent opacity={0} depthWrite={false} />
              </mesh>
            </group>
          )}
        </group>
      </group>
    );
  },
);

const BoxMesh: React.FC<{
  box: Window3DBoxGeometry;
  color: THREE.Color;
  selected?: boolean;
  transparent?: boolean;
  opacity?: number;
  depthWrite?: boolean;
  roughness?: number;
}> = ({
  box,
  color,
  selected = false,
  transparent = false,
  opacity = 1,
  depthWrite = true,
  roughness = 0.7,
}) => (
  <mesh
    position={[box.center.x, box.center.y, box.center.z]}
    rotation={[0, box.rotationY ?? 0, 0]}
    castShadow={!transparent}
    receiveShadow
  >
    <boxGeometry args={[box.size.x, box.size.y, box.size.z]} />
    <meshStandardMaterial
      color={color}
      transparent={transparent}
      opacity={opacity}
      depthWrite={depthWrite}
      roughness={roughness}
      metalness={0.03}
      side={THREE.DoubleSide}
    />
    <SelectedMeshOutline visible={selected} />
  </mesh>
);

function getPointerPointOnZPlane(
  event: ThreeEvent<PointerEvent>,
  z: number,
): THREE.Vector3 | null {
  return event.ray.intersectPlane(
    new THREE.Plane(new THREE.Vector3(0, 0, 1), -z),
    new THREE.Vector3(),
  );
}

function getWindowOpenAngleFromPointerPoint(
  assembly: Window3DAssemblyGeometry,
  leaf: Window3DLeafGeometry,
  point: THREE.Vector3,
): number {
  const localPoint = new THREE.Vector2(point.x, point.y)
    .sub(assembly.origin)
    .rotateAround(new THREE.Vector2(0, 0), -assembly.rotation);
  const relativePoint = localPoint.sub(leaf.hinge);
  if (relativePoint.lengthSq() < 1e-8)
    return clampWindowOpenAngle(leaf.rotation / leaf.rotationSign);

  const baseAngle = leaf.closedSign === 1 ? 0 : Math.PI;
  const pointerAngle = Math.atan2(relativePoint.y, relativePoint.x);
  const signedDelta = normalizeSignedAngle(pointerAngle - baseAngle);
  return clampWindowOpenAngle(signedDelta / leaf.rotationSign);
}

function normalizeSignedAngle(angle: number): number {
  let normalized = angle;
  while (normalized <= -Math.PI) normalized += Math.PI * 2;
  while (normalized > Math.PI) normalized -= Math.PI * 2;
  return normalized;
}

function applyOpenAngleToLeafGroup(
  leaf: Window3DLeafGeometry,
  leafGroup: THREE.Group | null,
  openAngle: number,
): void {
  if (!leafGroup) return;
  leafGroup.rotation.z = leaf.rotationSign * clampWindowOpenAngle(openAngle);
  leafGroup.updateMatrixWorld();
}

/** Resolves horizontal muntin count; defaults to 0. Honors legacy single-axis props. */
function resolveHorizontalMuntinCount(symbol: WindowType): number {
  const explicit =
    symbol.muntinHorizontalCount ??
    symbol.spröjsHorizontalCount ??
    symbol.threeD?.muntinHorizontalCount;
  if (explicit != null) return Math.max(0, explicit);

  const legacyCount =
    symbol.muntinCount ?? symbol.spröjsCount ?? symbol.threeD?.muntinCount;
  if (legacyCount == null) return 0;

  const orientation =
    symbol.muntinOrientation ??
    symbol.spröjsOrientation ??
    symbol.threeD?.muntinOrientation ??
    "horizontal";
  return orientation === "horizontal" ? Math.max(0, legacyCount) : 0;
}

/** Resolves vertical muntin count; defaults to 0. Honors legacy single-axis props. */
function resolveVerticalMuntinCount(symbol: WindowType): number {
  const explicit =
    symbol.muntinVerticalCount ??
    symbol.spröjsVerticalCount ??
    symbol.threeD?.muntinVerticalCount;
  if (explicit != null) return Math.max(0, explicit);

  const legacyCount =
    symbol.muntinCount ?? symbol.spröjsCount ?? symbol.threeD?.muntinCount;
  if (legacyCount == null) return 0;

  const orientation =
    symbol.muntinOrientation ??
    symbol.spröjsOrientation ??
    symbol.threeD?.muntinOrientation ??
    "horizontal";
  return orientation === "vertical" ? Math.max(0, legacyCount) : 0;
}

export default Window3DAssemblyMesh;